import { useEffect, useRef, useState } from "react";
import { Building2, Check } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { switchFranchise } from "@/redux/features/user/user.slice";
import { useGetMyFranchisesQuery } from "@/redux/services/restaurant/restaurant.api";
import { RestaurantFranchise } from "@/redux/services/restaurant/restaurant.type";
import { Roles } from "@/redux/services/user/user.type";
import { persistor } from "@/redux/store";
import { shouldShowFranchiseSwitcher } from "@/lib/franchise";
import { cn } from "@/lib/utils";

/**
 * Reloads the app on the landing page so every cached query, realtime
 * subscription and restaurant list is rebuilt for the new franchise
 * (the X-Franchise-Id header changes for every request).
 */
const reloadOnLanding = () => {
  window.location.assign("/");
};

/**
 * Franchise switcher for owners of two franchises or more (SFRESTO-169).
 * The selected franchise is sent as X-Franchise-Id (src/lib/baseQuery.ts); the
 * API then scopes the owner's access and the restaurant switcher to it.
 */
const FranchiseSwitcher: React.FC<{ onSwitched?: () => void }> = ({
  onSwitched = reloadOnLanding,
}) => {
  const dispatch = useAppDispatch();
  const { currentUser, currentFranchise } = useAppSelector(
    (state) => state.user
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner) ?? false;
  const { data: franchises } = useGetMyFranchisesQuery(undefined, {
    skip: !isOwner,
  });
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOwner || !shouldShowFranchiseSwitcher(franchises)) {
    return null;
  }

  const handleSelect = async (franchise: RestaurantFranchise) => {
    setIsOpen(false);
    if (franchise.id === currentFranchise?.id) {
      return;
    }
    // Also resets the current restaurant (it belongs to the old franchise).
    dispatch(switchFranchise(franchise));
    await persistor.flush();
    onSwitched();
  };

  return (
    <div className="relative hidden lg:block" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
        aria-label="Changer de franchise"
        title="Changer de franchise"
      >
        <Building2 className="h-5 w-5" />
        <span className="hidden xl:inline max-w-[140px] truncate text-sm font-medium">
          {currentFranchise?.name}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-border py-2 z-50">
          <p className="text-sm font-medium text-muted-foreground px-4 py-2">
            Vos franchises
          </p>
          <div className="flex flex-col max-h-80 overflow-y-auto">
            {franchises?.map((franchise) => {
              const isCurrent = franchise.id === currentFranchise?.id;
              return (
                <button
                  key={franchise.id}
                  onClick={() => handleSelect(franchise)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 transition-colors text-left",
                    isCurrent ? "bg-primary-light" : "hover:bg-muted"
                  )}
                >
                  <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-semibold text-sm">
                      {franchise.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium flex-1">
                    {franchise.name}
                  </span>
                  {isCurrent && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FranchiseSwitcher;
